"use client"

import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { TableMobileSort } from "./table-mobile-sort"
import styles from "./table.module.css"

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  /** Preserve horizontal comparisons with scroll; record lists default to cards below sm. */
  mobileLayout?: "cards" | "scroll"
  /** Style the existing scroll container, including bounded-height table panes. */
  containerClassName?: string
}

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  /** Plain label for an icon-only or custom-rendered heading. */
  mobileLabel?: string
  /** Internal label for the mobile replacement of a recognized sort control. */
  mobileSortLabel?: string
}

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  /** Overrides the inferred column label; an empty string suppresses it. */
  mobileLabel?: string
}

type TableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  /** Visually append a detail row to the preceding record's mobile card. */
  mobileVariant?: "continuation"
}

type Column = { label: string; id: string; sortButton?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>> }
const sortIconNames = new Set(["ArrowUpDown", "ArrowDownUp", "ArrowUp", "ArrowDown", "ChevronsUpDown"])
const TableContext = React.createContext<Column[] | null>(null)
const TableSectionContext = React.createContext<"head" | "body" | "foot">("body")

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, children, mobileLayout = "cards", containerClassName, ...props }, ref) => {
    const id = React.useId()
    const containerRef = React.useRef<HTMLDivElement>(null)
    const columns = mobileLayout === "cards" ? getColumns(children, id) : null
    const sortOptions = (columns ?? []).flatMap((column) => column.sortButton ? [{
      id: column.id,
      content: column.sortButton.props.children,
      disabled: column.sortButton.props.disabled,
    }] : [])

    function sortByHeader(headerId: string) {
      const header = Array.from(containerRef.current?.querySelectorAll("th") ?? []).find((element) => element.id === headerId)
      // Invoke the actual control so the page keeps its sort state, direction
      // toggling, pagination reset, and event handling in one place.
      header?.querySelector<HTMLButtonElement>("button")?.click()
    }

    return (
      <TableContext.Provider value={columns}>
        <div ref={containerRef} className={cn("relative w-full min-w-0 overflow-x-auto [scrollbar-width:thin]", containerClassName)}>
          <TableMobileSort options={sortOptions} onSort={sortByHeader} />
          <table
            ref={ref}
            role="table"
            className={cn("min-w-max w-full caption-bottom text-sm", className, columns && styles.cards)}
            data-mobile-layout={columns ? "cards" : "scroll"}
            {...props}
          >
            {children}
          </table>
        </div>
      </TableContext.Provider>
    )
  },
)
Table.displayName = "Table"

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <TableSectionContext.Provider value="head">
      <thead ref={ref} role="rowgroup" className={cn("[&_tr]:border-b", className)} {...props} />
    </TableSectionContext.Provider>
  ),
)
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <TableSectionContext.Provider value="body">
      <tbody ref={ref} role="rowgroup" className={cn("[&_tr:last-child]:border-0", className)} {...props} />
    </TableSectionContext.Provider>
  ),
)
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <TableSectionContext.Provider value="foot">
      <tfoot ref={ref} role="rowgroup" className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
    </TableSectionContext.Provider>
  ),
)
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, mobileVariant, ...props }, ref) => {
    const columns = React.useContext(TableContext)
    const section = React.useContext(TableSectionContext)

    return (
      <tr
        ref={ref}
        role="row"
        data-mobile-variant={mobileVariant}
        className={cn("border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted", className)}
        {...props}
      >
        {columns ? labelCells(children, columns, section) : children}
      </tr>
    )
  },
)
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, children, mobileLabel: _mobileLabel, mobileSortLabel, scope = "col", ...props }, ref) => (
    <th
      ref={ref}
      role={scope === "row" ? "rowheader" : "columnheader"}
      scope={scope}
      data-mobile-sort={mobileSortLabel ? "true" : undefined}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    >
      {mobileSortLabel ? (
        <>
          <span className={styles.sortHeadingLabel}>{mobileSortLabel}</span>
          <div className={styles.sortHeadingControl}>{children}</div>
        </>
      ) : children}
    </th>
  ),
)
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, children, mobileLabel, ...props }, ref) => {
    const columns = React.useContext(TableContext)
    return (
      <td ref={ref} role="cell" className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)} {...props}>
        {columns && mobileLabel ? <span className={styles.label} aria-hidden="true">{mobileLabel}</span> : null}
        {columns ? <div className={styles.value}>{children}</div> : children}
      </td>
    )
  },
)
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
  ),
)
TableCaption.displayName = "TableCaption"

// Only inspect the public compound structure, never execute consumer components.
// Opaque/multi-row headers retain a scrollable table instead of guessing labels.
function getColumns(children: React.ReactNode, id: string): Column[] | null {
  const headers = flattenChildren(children).filter((child) => React.isValidElement(child) && isTablePart(child.type, TableHeader))
  if (headers.length !== 1 || !React.isValidElement<{ children?: React.ReactNode }>(headers[0])) return null
  const rows = flattenChildren(headers[0].props.children)
  const row = rows[0]
  if (rows.length !== 1 || !React.isValidElement<{ children?: React.ReactNode }>(row) || !isTablePart(row.type, TableRow)) return null
  const cells = flattenChildren(row.props.children)
  if (!cells.length) return null
  const columns: Column[] = []
  for (const cell of cells) {
    if (!React.isValidElement<TableHeadProps>(cell) || !isTablePart(cell.type, TableHead)) return null
    if ((cell.props.colSpan ?? 1) !== 1 || (cell.props.rowSpan ?? 1) !== 1) return null
    columns.push({
      label: cell.props.mobileLabel ?? getLabelText(cell.props.children),
      id: cell.props.id ?? `${id}-column-${columns.length}`,
      sortButton: getSortButton(cell.props.children, cell.props["aria-sort"] !== undefined),
    })
  }
  return columns
}

function flattenChildren(children: React.ReactNode): React.ReactNode[] {
  return React.Children.toArray(children).flatMap((child) => {
    if (React.isValidElement<{ children?: React.ReactNode }>(child) && child.type === React.Fragment) {
      return flattenChildren(child.props.children)
    }
    return [child]
  })
}

function getSortButton(children: React.ReactNode, declaredSort: boolean): Column["sortButton"] {
  for (const child of React.Children.toArray(children)) {
    if (!React.isValidElement<React.ButtonHTMLAttributes<HTMLButtonElement>>(child)) continue
    const isButton = child.type === "button" || isTablePart(child.type, Button)
    if (isButton && child.props.onClick && getLabelText(child.props.children) && (declaredSort || hasSortIcon(child.props.children))) return child
    const nested = getSortButton(child.props.children, declaredSort)
    if (nested) return nested
  }
  return undefined
}

function hasSortIcon(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return false
    const type = child.type
    if ((typeof type === "object" || typeof type === "function") && type !== null && "displayName" in type && typeof type.displayName === "string" && sortIconNames.has(type.displayName)) return true
    return hasSortIcon(child.props.children)
  })
}

function getLabelText(children: React.ReactNode): string {
  return React.Children.toArray(children).map((child): string => {
    if (typeof child === "string" || typeof child === "number") return String(child)
    if (!React.isValidElement<{ children?: React.ReactNode; "aria-hidden"?: boolean | "true" | "false" }>(child)) return ""
    if (child.props["aria-hidden"] === true || child.props["aria-hidden"] === "true" || child.type === "svg") return ""
    return getLabelText(child.props.children)
  }).join(" ").replace(/\s+/g, " ").trim()
}

function labelCells(children: React.ReactNode, columns: Column[], section: "head" | "body" | "foot"): React.ReactNode {
  let index = 0
  // Preserve fragments and keys so toggling columns does not remount cell controls.
  function visit(nodes: React.ReactNode): React.ReactNode {
    return React.Children.map(nodes, (child) => {
      if (!React.isValidElement<TableCellProps>(child)) return child
      if (child.type === React.Fragment) return React.cloneElement(child, {}, visit(child.props.children))
      if (!isTablePart(child.type, TableCell) && !isTablePart(child.type, TableHead)) return child
      const span = child.props.colSpan ?? 1
      const column = columns[index]
      index += span
      if (!column) return child
      if (section === "head") return React.cloneElement(child as React.ReactElement<TableHeadProps>, {
        id: column.id,
        mobileSortLabel: column.sortButton ? column.label : undefined,
      })
      // Empty/loading/expanded rows span columns and have no individual field label.
      if (span !== 1) return child
      return React.cloneElement(child, {
        mobileLabel: child.props.mobileLabel ?? column.label,
        headers: child.props.headers ?? column.id,
      })
    })
  }
  return visit(children)
}

function isTablePart(type: unknown, component: { displayName?: string }): boolean {
  if (type === component) return true
  // Fast Refresh can retain elements with the previous forwardRef identity.
  // Use the same stable recognition for headers AND cells, or header discovery
  // falls back to scroll mode after an edit even though the structure is valid.
  if ((typeof type !== "object" && typeof type !== "function") || type === null || !("displayName" in type)) return false
  return Boolean(component.displayName) && type.displayName === component.displayName
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
